import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useSearchContext } from '@atlas/core';

import { SEARCH_DEBOUNCE_TIMER, searchQuerySymbol } from 'common/constants';
import { useSearch } from 'hooks/useSearch';

import { useSearchInputState } from './context/InputProvider';
import { useSimpleSearchState } from './context/SimpleProvider';
import SearchInput from './Input';

const SimpleSearchInput = () => {
  const { search } = useLocation();
  const { push } = useHistory();

  const { onSearch } = useSearchContext();
  const { isValidPrompt, searchString } = useSearch();
  const { setFocused } = useSearchInputState();
  const { setIsOpened } = useSimpleSearchState();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const onSearchHandler = useCallback(
    e => {
      const params = new URLSearchParams(searchParams);
      params.set(searchQuerySymbol, e.target.value);

      if (!params.get(searchQuerySymbol)) {
        params.delete(searchQuerySymbol);
      }

      push({ search: params.toString() });
    },
    [push, searchParams],
  );

  const onCleanHandler = useCallback(() => {
    searchParams.delete(searchQuerySymbol);
    push({ search: searchParams.toString() });
  }, [push, searchParams]);

  const onCancelHandler = useCallback(() => {
    searchParams.delete(searchQuerySymbol);
    push({ search: searchParams.toString() });
    setFocused(false);
    setIsOpened(false);
  }, [push, searchParams, setFocused, setIsOpened]);

  useEffect(() => {
    if (!isValidPrompt) return;

    const handler = setTimeout(() => {
      onSearch(searchString);
    }, SEARCH_DEBOUNCE_TIMER);

    return () => clearTimeout(handler);
  }, [isValidPrompt, searchString]);

  return (
    <SearchInput
      className="simplified"
      onCancel={onCancelHandler}
      onClean={onCleanHandler}
      onSearch={onSearchHandler}
    />
  );
};

export default SimpleSearchInput;
